'use client';
import { useEffect } from 'react';
import { Box, Typography, Container, Paper } from '@mui/material';
import Header from '../components/HomepageContent/Header';
import Footer from '../components/HomepageContent/Footer';
import Link from 'next/link';

export default function CookiePolicy() {
  useEffect(() => {
    document.title = 'CareAscendium - Cookie Policy';
  }, []);
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#fafafa', display: 'flex', flexDirection: 'column' }}>
      <Header />

      {/* Hero Section */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #153C7F 0%, #1E4A8C 50%, #2A5A9C 100%)',
        py: 8, 
        px: 4,
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background Pattern */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.1,
          backgroundImage: 'radial-gradient(circle at 25% 25%, white 2px, transparent 2px), radial-gradient(circle at 75% 75%, white 2px, transparent 2px)',
          backgroundSize: '50px 50px'
        }} />
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Typography 
            variant="h1" 
            sx={{ 
              color: 'white', 
              mb: 4,
              fontWeight: 800,
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              textShadow: '0 2px 4px rgba(0,0,0,0.3)'
            }}
          >
            Cookie Policy
          </Typography>
          <Typography 
            variant="h5" 
            sx={{ 
              color: 'rgba(255,255,255,0.9)',
              maxWidth: '900px',
              mx: 'auto',
              lineHeight: 1.6,
              fontWeight: 300,
              fontSize: { xs: '1.2rem', md: '1.5rem' }
            }}
          >
            How we use cookies to enhance your experience
          </Typography>
        </Container>
      </Box>

      {/* Cookie Policy Content */}
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Paper 
          elevation={0}
          sx={{ 
            p: { xs: 4, md: 6 },
            bgcolor: 'white',
            borderRadius: 3,
            border: '1px solid rgba(21,60,127,0.1)'
          }}
        >
          <Box sx={{ mb: 6 }}>
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 2,
                lineHeight: 2,
                color: '#333',
                fontSize: '1rem'
              }}
            >
              CareAscendium, LLC ("CareAscendium," "we," "us," or "our"), an Indiana limited liability company, uses cookies and similar technologies on our web and mobile application (collectively, the "Application") to enhance your experience, analyze usage, and provide functionality. This Cookie Policy explains what these technologies are, how we use them, and your choices. This policy supplements our Privacy Policy, which governs our overall data practices. By using the Application, you consent to our use of cookies as described here, subject to your opt-out choices.
            </Typography>
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography 
              variant="h4" 
              sx={{ 
                color: '#153C7F',
                mb: 3,
                fontWeight: 700,
                fontSize: '1.5rem'
              }}
            >
              What Are Cookies?
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 2,
                lineHeight: 2,
                color: '#333',
                fontSize: '1rem'
              }}
            >
              Cookies are small text files placed on your device when you visit a website or app. They store information about your preferences or actions to make the Application work efficiently. Similar technologies include web beacons, pixels, and local storage. We use both session cookies (deleted when you close the Application) and persistent cookies (remain until expired or deleted).
            </Typography>
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography 
              variant="h4" 
              sx={{ 
                color: '#153C7F',
                mb: 3,
                fontWeight: 700,
                fontSize: '1.5rem'
              }}
            >
              Types of Cookies We Use
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 2,
                lineHeight: 2,
                color: '#333',
                fontSize: '1rem'
              }}
            >
              We categorize cookies based on purpose:
            </Typography>
            <Box component="ul" sx={{ pl: 4, mb: 2 }}>
              <Typography component="li" sx={{ mb: 2, lineHeight: 2, color: '#333', fontSize: '1rem' }}>
                <strong>Essential Cookies:</strong> These are necessary for the Application to function. They enable basic features like navigation, secure login, and remembering your preferences (e.g., username/password for auto-login). Without these, the Application may not work properly. These cannot be disabled as they are essential.
              </Typography>
              <Typography component="li" sx={{ mb: 2, lineHeight: 2, color: '#333', fontSize: '1rem' }}>
                <strong>Analytics Cookies:</strong> These help us understand how users interact with the Application (e.g., pages visited, time spent). We use tools like Google Analytics (anonymized) to improve performance and features. No personal identifiers are shared.
              </Typography>
              <Typography component="li" sx={{ mb: 2, lineHeight: 2, color: '#333', fontSize: '1rem' }}>
                <strong>Functional Cookies:</strong> These enhance usability, such as remembering your settings or enabling push notifications.
              </Typography>
              <Typography component="li" sx={{ mb: 2, lineHeight: 2, color: '#333', fontSize: '1rem' }}>
                <strong>Advertising Cookies:</strong> We do not use third-party advertising cookies. If we introduce them in the future, we will update this policy and seek your consent.
              </Typography>
            </Box>
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 2,
                lineHeight: 2,
                color: '#333',
                fontSize: '1rem'
              }}
            >
              We do not use cookies for targeted advertising or to "sell" your data under laws like CCPA/CPRA.
            </Typography>
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography 
              variant="h4" 
              sx={{ 
                color: '#153C7F',
                mb: 3,
                fontWeight: 700,
                fontSize: '1.5rem'
              }}
            >
              How We Use Cookies
            </Typography>
            <Box component="ul" sx={{ pl: 4, mb: 2 }}>
              <Typography component="li" sx={{ mb: 2, lineHeight: 2, color: '#333', fontSize: '1rem' }}>
                <strong>Improve the Application:</strong> Analyze usage to fix bugs and add features.
              </Typography>
              <Typography component="li" sx={{ mb: 2, lineHeight: 2, color: '#333', fontSize: '1rem' }}>
                <strong>Personalization:</strong> Remember login details or preferences for convenience.
              </Typography>
              <Typography component="li" sx={{ mb: 2, lineHeight: 2, color: '#333', fontSize: '1rem' }}>
                <strong>Security:</strong> Detect fraud or unauthorized access.
              </Typography>
              <Typography component="li" sx={{ mb: 2, lineHeight: 2, color: '#333', fontSize: '1rem' }}>
                <strong>Aggregated Data:</strong> Use non-identifiable data for AI training or analytics (see Privacy Policy for details).
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography 
              variant="h4" 
              sx={{ 
                color: '#153C7F',
                mb: 3,
                fontWeight: 700,
                fontSize: '1.5rem'
              }}
            >
              Third-Party Cookies
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 2,
                lineHeight: 2,
                color: '#333',
                fontSize: '1rem'
              }}
            >
              We may use third-party services (e.g., analytics providers) that set their own cookies. These are governed by their privacy policies:
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 2,
                lineHeight: 2,
                color: '#333',
                fontSize: '1rem'
              }}
            >
              Google Analytics: <Link href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#153C7F', textDecoration: 'none', fontWeight: 600 }}>Privacy Policy</Link>
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 2,
                lineHeight: 2,
                color: '#333',
                fontSize: '1rem'
              }}
            >
              We require third parties to comply with this Policy and applicable laws.
            </Typography>
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography 
              variant="h4" 
              sx={{ 
                color: '#153C7F',
                mb: 3,
                fontWeight: 700,
                fontSize: '1.5rem'
              }}
            >
              Your Choices and Opt-Out
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 2,
                lineHeight: 2,
                color: '#333',
                fontSize: '1rem'
              }}
            >
              You can manage cookies through:
            </Typography>
            <Box component="ul" sx={{ pl: 4, mb: 2 }}>
              <Typography component="li" sx={{ mb: 2, lineHeight: 2, color: '#333', fontSize: '1rem' }}>
                <strong>Browser Settings:</strong> Most browsers allow you to block or delete cookies (e.g., Chrome: Settings &gt; Privacy &gt; Cookies). Note: Blocking essential cookies may impair Application functionality.
              </Typography>
              <Typography component="li" sx={{ mb: 2, lineHeight: 2, color: '#333', fontSize: '1rem' }}>
                <strong>Application Settings:</strong> Opt out of non-essential cookies or push notifications in-app.
              </Typography>
              <Typography component="li" sx={{ mb: 2, lineHeight: 2, color: '#333', fontSize: '1rem' }}>
                <strong>Global Privacy Control (GPC):</strong> We honor browser signals like GPC for opt-out of data sharing/sales.
              </Typography>
              <Typography component="li" sx={{ mb: 2, lineHeight: 2, color: '#333', fontSize: '1rem' }}>
                <strong>Do Not Sell/Share My Personal Information:</strong> For CA residents (CCPA), submit requests via <Link href="mailto:info@careascend.com" style={{ color: '#153C7F', textDecoration: 'none', fontWeight: 600 }}>info@careascend.com</Link>. We do not "sell" data but honor opt-outs for sharing.
              </Typography>
            </Box>
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 2,
                lineHeight: 2,
                color: '#333',
                fontSize: '1rem'
              }}
            >
              For questions, contact us (see below).
            </Typography>
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography 
              variant="h4" 
              sx={{ 
                color: '#153C7F',
                mb: 3,
                fontWeight: 700,
                fontSize: '1.5rem'
              }}
            >
              Changes to This Cookie Policy
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 2,
                lineHeight: 2,
                color: '#333',
                fontSize: '1rem'
              }}
            >
              We may update this Cookie Policy. Material changes will be notified via email or in-app, with 30 days' notice. Continued use constitutes acceptance. Check this page regularly.
            </Typography>
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography 
              variant="h4" 
              sx={{ 
                color: '#153C7F',
                mb: 3,
                fontWeight: 700,
                fontSize: '1.5rem'
              }}
            >
              Contact Information
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 2,
                lineHeight: 2,
                color: '#333',
                fontSize: '1rem'
              }}
            >
              If you have questions about this Cookie Policy, contact:
            </Typography>
            <Box sx={{ mt: 3, pl: 2, borderLeft: '3px solid #153C7F' }}>
              <Typography 
                variant="body1" 
                sx={{ 
                  mb: 1,
                  lineHeight: 2,
                  color: '#333',
                  fontSize: '1rem',
                  fontWeight: 600
                }}
              >
                CareAscendium, LLC
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  mb: 1,
                  lineHeight: 2,
                  color: '#333',
                  fontSize: '1rem'
                }}
              >
                Attn: Privacy and Security
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  mb: 1,
                  lineHeight: 2,
                  color: '#333',
                  fontSize: '1rem'
                }}
              >
                12486 Shadow Cove Way
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  mb: 1,
                  lineHeight: 2,
                  color: '#333',
                  fontSize: '1rem'
                }}
              >
                Carmel, IN 46033
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  mb: 1,
                  lineHeight: 2,
                  color: '#333',
                  fontSize: '1rem'
                }}
              >
                <Link 
                  href="mailto:info@careascend.com" 
                  style={{ 
                    color: '#153C7F',
                    textDecoration: 'none',
                    fontWeight: 600
                  }}
                >
                  info@careascend.com
                </Link>
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mt: 6, pt: 4, borderTop: '1px solid rgba(21,60,127,0.1)' }}>
            <Typography 
              variant="body2" 
              sx={{ 
                mb: 2,
                color: '#666',
                fontSize: '0.9rem',
                fontStyle: 'italic'
              }}
            >
              Last Updated: October 30, 2025
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                mb: 2,
                color: '#666',
                fontSize: '0.9rem',
                fontWeight: 600
              }}
            >
              © CAREASCENDIUM, LLC
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                mb: 1,
                color: '#666',
                fontSize: '0.9rem'
              }}
            >
              <Link 
                href="https://www.CareAscendium.com" 
                style={{ 
                  color: '#153C7F',
                  textDecoration: 'none'
                }}
              >
                www.CareAscendium.com
              </Link>
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                mb: 1,
                color: '#666',
                fontSize: '0.9rem',
                fontStyle: 'italic'
              }}
            >
              Confidential and Proprietary
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                mb: 1,
                color: '#666',
                fontSize: '0.9rem'
              }}
            >
              <Link 
                href="mailto:info@CareAscendium.com" 
                style={{ 
                  color: '#153C7F',
                  textDecoration: 'none'
                }}
              >
                info@CareAscendium.com
              </Link>
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#666',
                fontSize: '0.9rem'
              }}
            >
              12486 Shadow Cove Way, Carmel, IN 46033
            </Typography>
          </Box>
        </Paper>
      </Container>
      <Footer />
    </Box>
  );
}

